/**
 * Labstep
 *
 * @module hoc/Params
 * @desc HOC that enhances component with params
 * capabilities. Allows for params to be pushed
 * to url.
 */

import { withActiveGroup } from 'labstep-web/containers/ActiveGroup';
import { withUiPersistent } from 'labstep-web/containers/UiPersistent';
import { ParamsContextProvider } from 'labstep-web/contexts/params';
import { useParamsStateContext } from 'labstep-web/contexts/params-state/hook';
import type {
  EntityViewParameters,
  PostFilterNodeType,
} from 'labstep-web/models/entity-view.model';
import { PostFilter } from 'labstep-web/services/postFilter';
import type { PostFilterOperatorType } from 'labstep-web/services/postFilter/constants';
import { PostFilterOperator } from 'labstep-web/services/postFilter/constants';
import { QueryParameterService } from 'labstep-web/services/query-parameter.service';
import {
  makeSelectParams,
  setParams,
} from 'labstep-web/state/slices/paramsHoc';
import store from 'labstep-web/state/store';
import { isEqual } from 'lodash';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import qs from 'query-string';
import React from 'react';
import { useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import type {
  IParamsContainerProps,
  IParamsContainerState,
  IParamsHOCContainerProps,
} from './types';

export const POST_FILTER_PAGE_SIZE = 10;

const ignoreUiSearchParams = ['page', 'serializerGroups'];
const ignoreHistoryParams = ['m'];

/** List of parameter fields supported with filter */
export const filterParams = [
  'search_query',
  'page',
  'filter',
  'group_id',
  'sort',
  'skip_total',
  'at',
  'serializerGroups',
];

export class ParamsContainer extends React.Component<
  IParamsContainerProps,
  IParamsContainerState
> {
  public constructor(props: IParamsContainerProps) {
    super(props);

    // Initialize state in constructor
    this.initialize();

    this.setParams = this.setParams.bind(this);
    this.addPostFilter = this.addPostFilter.bind(this);
    this.removePostFilter = this.removePostFilter.bind(this);
    this.clearParams = this.clearParams.bind(this);
    this.clearAll = this.clearAll.bind(this);
    this.getCurrentSearchParams =
      this.getCurrentSearchParams.bind(this);
    this.setPostFilterNodeType =
      this.setPostFilterNodeType.bind(this);
  }

  public initialize() {
    const { location, historyAction, initialParams } = this.props;

    if (this.props.paramsStateContext?.name) {
      // When using paramsStateContext, try to load from uiPersistent first
      // This ensures filters are remembered across navigation when using usePostFilter
      const uiPersistentParams =
        this.props.uiPersistent?.search_params?.[location.pathname];
      const initialSearchParams =
        this.props.paramsStateContext?.initialSearchParams || {};

      // Merge uiPersistent params with initialSearchParams
      // uiPersistent takes precedence for user-controlled params, but we preserve system filters from initialSearchParams
      let searchParams = { ...initialSearchParams };

      if (
        uiPersistentParams &&
        Object.keys(uiPersistentParams).length > 0
      ) {
        // Merge the params, preserving system filters from initialSearchParams if present
        searchParams = {
          ...initialSearchParams,
          ...uiPersistentParams,
        };

        // Special handling for filter param to merge system filters (branch 1) with user filters (branch 0)
        if (uiPersistentParams.filter && initialSearchParams.filter) {
          const uiFilter =
            uiPersistentParams.filter as EntityViewParameters['filter'];
          const initialFilter =
            initialSearchParams.filter as EntityViewParameters['filter'];

          // Preserve user filters from uiPersistent and system filters from initialSearchParams
          searchParams.filter = [
            (uiFilter?.[0] || initialFilter?.[0]) as any, // User filters from uiPersistent
            (initialFilter?.[1] || uiFilter?.[1]) as any, // System filters from initialSearchParams
          ];
        }
      }

      this.state = {
        ready: true,
        searchParams,
      };
      store.dispatch(
        setParams({
          name: this.props.paramsStateContext.name,
          params: searchParams,
        }),
      );
    } else if (historyAction) {
      this.state = {
        ready: false,
      };
      if (
        initialParams &&
        Object.keys(qs.parse(location.search)).length === 0
      ) {
        this.addParamsToHistory(initialParams, historyAction);
      }
    } else {
      this.state = {
        ready: true,
        searchParams:
          QueryParameterService.sanitizeParams(initialParams),
      };
    }
  }

  public componentDidMount(): void {
    const { initialParams } = this.props;

    // Handle the case where we need to set initial params but couldn't do it in constructor
    if (
      this.state?.ready &&
      initialParams &&
      !this.props.paramsStateContext?.name &&
      !this.props.historyAction
    ) {
      this.setParams(initialParams);
    }

    this.setState({ ready: true });
  }

  public componentDidUpdate(prevProps: IParamsContainerProps): void {
    const { initialParams, historyAction, location } = this.props;

    // Handle case where initialParams are provided late (e.g., from uiPersistent loading asynchronously)
    // Only apply if historyAction is set and URL has no params
    if (
      historyAction &&
      initialParams &&
      !isEqual(prevProps.initialParams, initialParams) &&
      Object.keys(qs.parse(location.search)).length === 0
    ) {
      this.addParamsToHistory(initialParams, historyAction);
    }
  }

  /**
   * Updates search params. If a param is passed with an undefined value
   * it will be removed from the params.
   * @param  {object} params - object of key values of params to be updated
   */
  public setParams(params: Record<string, any>): void {
    const searchParams = this.getCurrentSearchParams();

    const updatedParams = { ...searchParams, ...params };

    // Pagination specific
    const paramsKeys = Object.keys(params);
    if (!(paramsKeys.length === 1 && paramsKeys[0] === 'page')) {
      updatedParams.page = undefined;
    }

    // Removes undefined params
    const cleanedUpdateParams =
      QueryParameterService.sanitizeParams(updatedParams);

    this.updateParams(cleanedUpdateParams);
  }

  public getCurrentSearchParams() {
    const { historyAction, location, initialParams } = this.props;
    const { ready } = this.state;

    if (this.props.paramsStateContext?.name) {
      return { ...this.props.reduxSearchParams };
    }

    let params = { ...this.state.searchParams };

    if (historyAction) {
      params = qs.parse(location.search);
      params = omit(params, ignoreHistoryParams);
      if (initialParams && !ready) {
        // https://github.com/Labstep/labstep/issues/7672
        // Need to pass initialParams so that AsyncInput gets the correct
        // defaultValue from search_query on mount
        params = { ...params, ...initialParams };
      }
    }

    return QueryParameterService.sanitizeParams(params);
  }

  public setPostFilterNodeType(
    type: PostFilterOperatorType,
    branch: number = 0,
  ): void {
    const searchParams = this.getCurrentSearchParams();
    const postFilter =
      searchParams.filter as EntityViewParameters['filter'];
    if (!postFilter) {
      return;
    }

    const updatedParams: EntityViewParameters = {
      ...searchParams,
      filter: [
        branch === 0 ? { ...postFilter[0], type } : postFilter[0],
        branch === 1 ? { ...postFilter[1], type } : postFilter[1],
      ],
    };

    const cleanedUpdateParams =
      QueryParameterService.sanitizeParams(updatedParams);
    this.updateParams(cleanedUpdateParams);
  }

  private addParamsToHistory = (
    params: Record<string, unknown>,
    historyAction: 'replace',
  ): void => {
    const { location, history } = this.props;
    const currentParams = qs.parse(location.search);
    const ignoredParams = pick(currentParams, ignoreHistoryParams);
    const search = `?${qs.stringify({
      ...ignoredParams,
      ...params,
    })}`;
    history[historyAction]({
      pathname: location.pathname,
      search,
    });
  };

  /**
   * Add a filter node to the post filter of the current search params
   * @param filterNode Filter node to add
   * @param replace Whether to replace an existing filter node with the same path and attribute
   * @param branch 0 or 1, which branch of the post filter to add the filter node to.
   * 0 is the user-controlled branch, 1 is the system-controlled branch
   */
  public addPostFilter(
    filterNode: PostFilterNodeType,
    replace: boolean | number = true,
    branch: number = 0,
  ): void {
    const searchParams = this.props.reduxSearchParams || {};
    const updatedSearchParams = { ...searchParams };

    const filter: EntityViewParameters['filter'] =
      (updatedSearchParams.filter as EntityViewParameters['filter']) || [
        {
          type: PostFilterOperator.and,
          predicates: [],
        },
        {
          type: PostFilterOperator.and,
          predicates: [],
        },
      ];
    let newPredicates =
      filter && filter[branch].predicates
        ? [...filter[branch].predicates]
        : [];

    if (replace !== undefined && replace !== false) {
      if (typeof replace === 'number') {
        newPredicates.splice(replace, 1, filterNode);
      } else {
        const index = newPredicates.findIndex((node) => {
          return PostFilter.compareNodes(node, filterNode);
        });
        if (index !== -1) {
          newPredicates = [
            ...newPredicates.slice(0, index),
            filterNode,
            ...newPredicates.slice(index + 1),
          ];
        } else {
          newPredicates.push(filterNode);
        }
      }
    }

    const updatedBranch = filter
      ? {
          ...filter[branch],
          predicates:
            replace !== undefined && replace !== false
              ? newPredicates
              : [...newPredicates, filterNode],
        }
      : {
          type: PostFilterOperator.and,
          predicates: [filterNode],
        };
    const updatedFilter = [
      branch === 0 ? updatedBranch : filter![0],
      branch === 1 ? updatedBranch : filter![1],
    ];
    updatedSearchParams.filter = updatedFilter;
    const sanitizedParams = QueryParameterService.sanitizeParams(
      updatedSearchParams,
    );
    this.updateParams(sanitizedParams);
  }

  /**
   * Remove filter node
   * @param index Index of filter node to remove
   * @param branch Which branch to remove the node from. 0 is the user-controlled branch, 1 is the system-controlled branch.
   */
  public removePostFilter(index: number, branch: number = 0): void {
    const searchParams = this.props.reduxSearchParams || {};
    const updatedParams = { ...searchParams };
    const filter =
      searchParams.filter as EntityViewParameters['filter'];
    if (!filter || !filter[branch] || !filter[branch].predicates) {
      return;
    }

    const predicates = [...filter[branch].predicates];
    predicates.splice(index, 1);
    const updatedFilterBranch = { ...filter[branch], predicates };
    const updatedFilter: EntityViewParameters['filter'] = [
      branch === 0 ? updatedFilterBranch : filter[0],
      branch === 1 ? updatedFilterBranch : filter[1],
    ];

    if (
      filter[0].predicates &&
      filter[0].predicates.length !== 0 &&
      updatedFilter[0].predicates &&
      updatedFilter[0].predicates.length === 0
    ) {
      this.clearAll();
      return;
    }

    updatedParams.filter = updatedFilter;
    const sanitizedParams =
      QueryParameterService.sanitizeParams(updatedParams);
    this.updateParams(sanitizedParams);
  }

  /**
   * Clears all search params
   */
  public clearAll(ignore: string[] = []): void {
    let params: any = {};
    const searchParams = this.getCurrentSearchParams();
    if (Array.isArray(ignore)) {
      params = pick(searchParams, ignore);
    }
    if (searchParams.filter) {
      params.filter = [
        {
          type: PostFilterOperator.and,
          predicates: [],
        },
        (searchParams.filter as Record<number, unknown>)[1],
      ];
    }
    if (!isEqual(params, searchParams)) {
      this.updateParams(params);
    }
  }

  /**
   * Clears params
   * @param  {array} params - array of keys to be cleared
   */
  public clearParams(params: string[]): void {
    const searchParams = this.getCurrentSearchParams();
    const updatedParams = omit(searchParams, params);
    this.updateParams(updatedParams);
  }

  public updateParams(params: Record<string, any>): void {
    const { location, historyAction, setUiSearchParams } = this.props;
    if (
      Object.keys(params).length !== 0 &&
      !('search_query' in params) &&
      this.props.paramsStateContext?.destination
    ) {
      store.dispatch(
        setParams({
          name: this.props.paramsStateContext.destination,
          params,
        }),
      );

      // Also store in uiPersistent for persistence across navigation
      const uiSearchParams = omit(params, ignoreUiSearchParams);
      setUiSearchParams(location.pathname, uiSearchParams);

      return;
    }
    if (
      Object.keys(params).length === 0 &&
      this.props.paramsStateContext?.destination
    ) {
      store.dispatch(
        setParams({
          name: this.props.paramsStateContext.destination,
          params,
        }),
      );

      // Also store in uiPersistent for persistence across navigation
      const uiSearchParams = omit(params, ignoreUiSearchParams);
      setUiSearchParams(location.pathname, uiSearchParams);

      return;
    }
    if (this.props.paramsStateContext?.name) {
      store.dispatch(
        setParams({
          name: this.props.paramsStateContext.name,
          params,
        }),
      );

      // Also store in uiPersistent for persistence across navigation
      const uiSearchParams = omit(params, ignoreUiSearchParams);
      setUiSearchParams(location.pathname, uiSearchParams);

      return;
    }
    if (historyAction) {
      this.addParamsToHistory(params, historyAction);
      const uiSearchParams = omit(params, ignoreUiSearchParams);
      setUiSearchParams(location.pathname, uiSearchParams);
    } else {
      this.setState({ searchParams: params });
    }
  }

  public render() {
    if (!this.state?.ready) {
      return null;
    }

    return (
      <ParamsContextProvider
        value={{
          globalParams: this.props.paramsStateContext?.globalParams,
          initialSearchParams:
            this.props.paramsStateContext?.initialSearchParams || {},
          injectFields:
            this.props.paramsStateContext?.injectFields || {},
          searchParams: this.getCurrentSearchParams(),
          setParams: this.setParams,
          clearAll: this.clearAll,
          clearParams: this.clearParams,
          addPostFilter: this.addPostFilter,
          setPostFilterNodeType: this.setPostFilterNodeType,
          removePostFilter: this.removePostFilter,
        }}
        children={this.props.children}
      />
    );
  }
}

export const ParamsHOCChildren = withActiveGroup(
  withUiPersistent(withRouter(ParamsContainer)),
);

export const ParamsHOC: RFC<IParamsHOCContainerProps> = (props) => {
  const paramsStateContext = useParamsStateContext();

  const reduxSearchParams = useSelector(
    makeSelectParams(
      paramsStateContext?.destination || paramsStateContext?.name,
    ),
  );

  return (
    <ParamsHOCChildren
      {...props}
      paramsStateContext={paramsStateContext}
      reduxSearchParams={
        reduxSearchParams as Record<string, unknown> | null
      }
    />
  );
};
